import React from "react";

import { Controller, useForm } from "react-hook-form";
import slugify from "slugify";

import {
  useCreateSdkBundle,
  useUpdateSdkBundle,
} from "src/api/connectApi/queries";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import {
  ProviderSdkBundleResponse,
  ProviderSdkConfigResponse,
} from "src/clients/sdk";
import { getSdkBundleErrorDescription } from "src/connections/sdk/bundleErrors";
import { SdkScriptSelect } from "src/connections/sdk/SdkScriptSelect";
import {
  BUNDLE_RULE_DESCRIPTION,
  buildBundleOptions,
} from "src/connections/sdk/scriptOptions";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { SLUG_ALLOWED_CHARACTERS } from "src/utils/validations";

type BundleMode = "existing" | "new";

type ScriptFormValues = {
  bundleMode: BundleMode;
  existingBundleId: string;
  newBundleName: string;
  newBundleSlug: string;
  scriptIds: string[];
};

const MAX_BUNDLE_SLUG_LENGTH = 80;

/** Mirrors the slug conventions of `FlowSlugInput` (lowercase, hyphenated). */
const validateBundleSlug = (value: string): true | string => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return "Bundle slug cannot be empty";
  if (trimmed !== value)
    return "Bundle slug cannot have leading or trailing spaces";
  if (trimmed.length > MAX_BUNDLE_SLUG_LENGTH)
    return `Bundle slug has to be at most ${MAX_BUNDLE_SLUG_LENGTH} characters long`;
  if (value !== value.toLowerCase()) return "Bundle slug must be lowercase";
  if (!SLUG_ALLOWED_CHARACTERS.test(value))
    return "Bundle slug is not valid. Please only use lowercase letters, digits and dashes.";
  return true;
};

type Props = {
  open: boolean;
  /** The SDK Script being added to a bundle. */
  script?: ProviderSdkConfigResponse;
  /** All SDK Scripts, used for the "Create new Bundle" multi-select. */
  configs: ProviderSdkConfigResponse[];
  /** Existing bundles, used for the "Add to existing Bundle" select. */
  bundles: ProviderSdkBundleResponse[];
  baseUrl: string | undefined;
  onClose: () => void;
  afterLeave?: () => void;
};

export const SdkScriptModal: React.FC<Props> = ({
  open,
  script,
  configs,
  bundles,
  baseUrl,
  onClose,
  afterLeave,
}) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ScriptFormValues>({
    values: {
      bundleMode: "existing",
      existingBundleId: "",
      newBundleName: "",
      newBundleSlug: "",
      scriptIds: script ? [script.id] : [],
    },
  });

  const bundleMode = watch("bundleMode");

  const createBundle = useCreateSdkBundle(baseUrl);
  const updateBundle = useUpdateSdkBundle(baseUrl);

  const bundleOptions = buildBundleOptions(bundles, script);

  const addToExistingBundle = async (bundleId: string) => {
    const bundle = bundles.find((candidate) => candidate.id === bundleId);
    // Throw instead of returning: onSubmit closes the modal once this resolves,
    // so bailing silently would look like the script had been added.
    if (!bundle || !script) {
      throw new Error("The selected SDK Bundle is no longer available.");
    }

    const configIds = Array.from(
      new Set([...bundle.configs.map((config) => config.id), script.id]),
    );
    await updateBundle.mutateAsync({
      id: bundle.id,
      payload: { name: bundle.name, config_ids: configIds },
    });
    toastActions.success({
      title: "Added to SDK Bundle",
      description: `${script.name} was added to ${bundle.name}.`,
    });
  };

  const createNewBundle = async (
    name: string,
    slug: string,
    scriptIds: string[],
  ) => {
    await createBundle.mutateAsync({
      name,
      slug,
      config_ids: scriptIds,
    });
    toastActions.success({
      title: "SDK Bundle created",
      description: `Bundle ${name} was created.`,
    });
  };

  const onSubmit = async (values: ScriptFormValues) => {
    try {
      if (values.bundleMode === "existing") {
        await addToExistingBundle(values.existingBundleId);
      } else {
        // Always include the script the merge action was opened for, even if
        // the user cleared it from the multi-select (mirrors add-to-existing).
        const scriptIds = Array.from(
          new Set(script ? [script.id, ...values.scriptIds] : values.scriptIds),
        );
        await createNewBundle(
          values.newBundleName,
          values.newBundleSlug,
          scriptIds,
        );
      }
      onClose();
    } catch (error) {
      toastActions.failure({
        title: "Failed to add SDK Script to a Bundle",
        description: getSdkBundleErrorDescription(
          error,
          "The SDK Script could not be added to a bundle. Please try again.",
        ),
      });
    }
  };

  return (
    <Modal afterLeave={afterLeave} open={open} onClose={onClose}>
      <Modal.Header>Add SDK Script to a Bundle</Modal.Header>
      <form data-loc="sdk-script-form" onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <FormItem gap="sm">
            <Controller
              control={control}
              name="bundleMode"
              render={({ field: { value, onChange } }) => (
                <SimpleRadioGroup
                  className="space-x-3"
                  orientation="horizontal"
                  value={value}
                  onValueChange={onChange}
                >
                  <SimpleRadioGroup.Item
                    dataLoc="sdk-script-bundle-existing"
                    label="Add to existing Bundle"
                    labelClassName="ml-2"
                    value="existing"
                  />
                  <SimpleRadioGroup.Item
                    dataLoc="sdk-script-bundle-new"
                    label="Create new Bundle"
                    labelClassName="ml-2"
                    value="new"
                  />
                </SimpleRadioGroup>
              )}
            />
          </FormItem>

          {bundleMode === "existing" ? (
            <FormItem
              description={BUNDLE_RULE_DESCRIPTION}
              gap="sm"
              label="Select a Bundle"
              isRequired
            >
              {Boolean(errors.existingBundleId) && (
                <ErrorHint>{errors.existingBundleId?.message}</ErrorHint>
              )}
              <Controller
                control={control}
                name="existingBundleId"
                render={({ field: { value, onChange } }) => (
                  <Select
                    dataLoc="sdk-script-existing-bundle"
                    options={bundleOptions}
                    placeholder="Select an SDK Bundle to add"
                    value={value}
                    fullWidth
                    searchable
                    onChange={onChange}
                  />
                )}
                rules={{
                  validate: (value) =>
                    bundleMode !== "existing" ||
                    Boolean(value) ||
                    "Select a bundle",
                }}
              />
            </FormItem>
          ) : (
            <>
              <FormItem gap="sm" label="Name" isRequired>
                {Boolean(errors.newBundleName) && (
                  <ErrorHint>{errors.newBundleName?.message}</ErrorHint>
                )}
                <Input
                  data-loc="sdk-script-new-bundle-name"
                  placeholder="New SDK Bundle"
                  {...register("newBundleName", {
                    onChange: (event) =>
                      setValue(
                        "newBundleSlug",
                        slugify(event.target.value, {
                          strict: true,
                          lower: true,
                        }),
                        { shouldValidate: true },
                      ),
                    validate: (value) =>
                      bundleMode !== "new" ||
                      value.trim().length > 0 ||
                      "Name is required",
                  })}
                  fullWidth
                />
              </FormItem>
              <FormItem gap="sm" label="URL Identifier" isRequired>
                {Boolean(errors.newBundleSlug) && (
                  <ErrorHint>{errors.newBundleSlug?.message}</ErrorHint>
                )}
                <Input
                  data-loc="sdk-script-new-bundle-slug"
                  placeholder="Enter URL Identifier (this cannot be edited)"
                  {...register("newBundleSlug", {
                    validate: (value) =>
                      bundleMode !== "new" || validateBundleSlug(value),
                  })}
                  fullWidth
                />
              </FormItem>
              <FormItem
                description={BUNDLE_RULE_DESCRIPTION}
                gap="sm"
                label="Select SDK Scripts"
                isRequired
              >
                {Boolean(errors.scriptIds) && (
                  <ErrorHint>{errors.scriptIds?.message}</ErrorHint>
                )}
                <Controller
                  control={control}
                  name="scriptIds"
                  render={({ field: { value, onChange } }) => (
                    <SdkScriptSelect
                      configs={configs}
                      dataLoc="sdk-script-scripts"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                  rules={{
                    validate: (value) =>
                      bundleMode !== "new" ||
                      value.length > 0 ||
                      "Select at least one SDK Script",
                  }}
                />
              </FormItem>
            </>
          )}
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="sdk-script-save"
              isDisabled={isSubmitting}
              htmlType="submit"
              variant="primary"
            >
              Add
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
